import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
// components
import Dropdown from '../components/Structures/Dropdown';
import CustomInput from '../components/Common/CustomInput';
import CustomButton from '../components/Common/CustomButton';
import MainLoader from '../components/Structures/MainLoader';

const KeywordsConfigForm = () => {
  const { state } = useParams();
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // New state to store the selected contact from the dropdown
  const [selectedAlertTrack, setSelectedAlertTrack] = useState('');

  const { searchTerm, auth, alertTracks } = useSelector((state) => state.user);
  const parsedUserId = parseInt(auth, 10) || null;

  // Convert the alertTracks array into options for the Dropdown
  const alertTracksOptions =
    alertTracks?.map((track) => ({
      value: track.Contact,
      label: `${track.Name} - ${track.Contact}`,
    })) || [];

  useEffect(() => {
    const fetchConfig = async () => {
      if (parsedUserId) {
        setLoading(true);
        try {
          const response = await axios.get(`/api/${state.toUpperCase()}/alerts/keywords-config/${parsedUserId}`);
          if (response.data && response.data.data) {
            const record = response.data.data;
            // If the record contains a contact, initialize the dropdown with it
            const contact = record.Email || record.PhoneNumber;
            if (contact) {
              setSelectedAlertTrack(contact);
            }
            try {
              const loadedKeywords = JSON.parse(record.Keywords);
              // Convert each keyword into an object { value, editable: false }
              const keywordObjects = loadedKeywords.map((kw) => ({ value: kw, editable: false }));
              setKeywords(keywordObjects);
            } catch (error) {
              console.error('Error parsing keywords:', error);
              setKeywords([]);
            } finally {
              setLoading(false);
            }
          }
        } catch (error) {
          console.error('Error fetching keywords config:', error);
          setLoading(false);
        }
      }
    };

    fetchConfig();
  }, [state, parsedUserId]);

  const handleKeywordChange = (index, value) => {
    const updatedKeywords = keywords.map((keyword, idx) => {
      if (idx === index) {
        return { ...keyword, value };
      }
      return keyword;
    });
    setKeywords(updatedKeywords);
  };

  const handleAddKeyword = () => {
    // Add a new keyword object with editable: true so the user can input the value
    setKeywords([...keywords, { value: '', editable: true }]);
  };

  const handleRemoveKeyword = (index) => {
    const updatedKeywords = keywords.filter((_, idx) => idx !== index);
    setKeywords(updatedKeywords);
  };

  // Handler for the Dropdown change event
  const handleDropdownChange = (e) => {
    setSelectedAlertTrack(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    // Validate and prepare the keywords array, filtering out empty values
    const keywordsArray = keywords.map((kw) => kw.value.trim()).filter((kw) => kw !== '');

    // Check if the selected contact is an email (contains "@") or a phone number
    const isEmail = selectedAlertTrack.includes('@');

    const payload = {
      userId: parsedUserId,
      token: searchTerm,
      phoneNumber: isEmail ? '' : selectedAlertTrack,
      email: isEmail ? selectedAlertTrack : '',
      keywords: keywordsArray,
    };

    try {
      await axios.post(`/api/${state.toUpperCase()}/alerts/keywords-config`, payload);
      setMessage('Record created/updated successfully!');
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <MainLoader />;

  return (
    <div className="bg-white border border-border 2xl:p-5 xl:p-5 p-5 w-full mb-[20px] ">
      <div className="border-b border-border mb-3.5 pb-3.5 flex flex-col md:flex-row max-md:gap-4 items-center justify-between">
        <h2 id="Office-Information" className="font-playfair text-[28px] text-darkblue font-medium leading-8">
          Keywords Setup
        </h2>
      </div>
      <div className="flex flex-col justify-center items-center">
        <form onSubmit={handleSubmit} className="space-y-4 sm:w-1/2">
          {/* Replace phone and email inputs with a Dropdown for selecting an alert track */}
          <Dropdown
            id="alertTrack"
            label="Select Alert Track"
            title="Select a contact"
            options={alertTracksOptions}
            onChange={handleDropdownChange}
            value={selectedAlertTrack}
          />
          <div>
            <label className="block mb-2 font-medium">Keywords</label>
            {keywords.map((keyword, index) => (
              <div key={index} className="flex items-center mb-2">
                <CustomInput
                  type="text"
                  value={keyword.value}
                  onChange={(e) => handleKeywordChange(index, e.target.value)}
                  placeholder="Enter keyword"
                  disabled={!keyword.editable}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(index)}
                  className="ml-2 p-2 border border-red-500 text-red-500"
                >
                  x
                </button>
              </div>
            ))}
            <button type="button" onClick={handleAddKeyword} className="mt-2 p-2 bg-blue-500 text-white">
              Add a new keyword
            </button>
          </div>
          <CustomButton type="submit" fullWidth>
            {loading ? 'Saving...' : 'Save Keywords'}
          </CustomButton>
          {message && <p className="text-center mt-4">{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default KeywordsConfigForm;
